/**
 * POINT-AND-CLICK EXECUTION: a click on the chart becomes a discretionary order.
 *
 * <h2>Why a pure resolver rather than a handler</h2>
 *
 * Every interesting decision here is about whether a click COUNTS and which bar it lands on, and
 * none of it needs a canvas: `convertFromPixel` is the only part that does, and it is three lines
 * in the overlay. So the rules live in a function of an event-shaped object, which is where the
 * tests are — the same division `resolveHotkey` already follows for the keyboard.
 *
 * <h2>ALT, not SHIFT — and that is a collision, not a preference</h2>
 *
 * The brief suggested Shift+Click. Shift+Click is ALREADY the spiral inspection gesture: a
 * capture-phase listener on the chart wrapper intercepts it, calls `stopPropagation`, and opens
 * the bar inspection menu. A handler bound here would never see the event at all — the gesture
 * would silently do the other thing, which is the worst available outcome.
 *
 * <p>Ctrl and Meta are out for the same kind of reason: Ctrl+Click is a right-click on macOS, and
 * right-click is bound to that same inspection menu. Alt is the one modifier the chart does not
 * already claim.
 *
 * <p>So the gesture is <b>Alt+Click</b>, and WHICH order it places is a pen the reader selects in
 * the HUD — Buy, Sell or Flat. One modifier plus a pen rather than three modifiers, because the
 * third combination would have to be Alt+Shift and Shift is the one that is taken.
 *
 * <h2>THE FILL IS THE BAR'S CLOSE, not the clicked price — a stated deviation</h2>
 *
 * The brief asks for a trade "at that exact bar and price point". The bar is honoured; the price
 * is not, and it must not be: a click resolves to an arbitrary point inside the bar's range, so
 * filling there would let an operator buy every low and sell every high of bars they can already
 * see. That is not a worse fill model, it is a different and unbeatable strategy, and it would
 * make the ALGO vs HUMAN comparison meaningless in the human's favour.
 *
 * <p>The brief's own next sentence is what settles it: the injected trade must "update the running
 * manual metrics identically to a keyboard-driven execution". A keyboard execution fills at the
 * playhead bar's close. So does this.
 *
 * <h2>A click BEHIND the playhead is refused, not executed</h2>
 *
 * Those bars are drawn, so their outcome is known. Trading one is hindsight, and the whole replay
 * exists to prevent exactly that. The refusal is RECORDED rather than silent — a click that does
 * nothing and says nothing is indistinguishable from a broken chart.
 *
 * <h2>A click AHEAD of the playhead is a JUMP, and it is information-free</h2>
 *
 * Price is revealed only up to the playhead, so the canvas ahead of it is empty: a reader clicking
 * there is saying "advance to about here and trade", with nothing to go on but the calendar and
 * the structural overlays — which were known at bar one. The clock jumps, which reveals every bar
 * in between, and the order fills at the bar it lands on. That composes with the transport's own
 * `playTo` rather than inventing a second way to move the playhead.
 */

/** The orders a click can place. Same three the buttons and the hotkeys use. */
export const CLICK_PENS = Object.freeze(['BUY', 'SELL', 'FLAT']);

/** The default pen: the order a reader is most likely to want, and the least destructive. */
export const DEFAULT_CLICK_PEN = 'BUY';

/**
 * Should this click place an order, and where?
 *
 * @param event          anything with `altKey`, `shiftKey`, `ctrlKey`, `metaKey`
 * @param pen            'BUY' | 'SELL' | 'FLAT'
 * @param barIndex       the bar the click resolved to, or null when it resolved to nothing
 * @param playheadIndex  the current frame
 * @param barCount       how many bars the axis holds
 * @param enabled        false while the session is frozen, or before it goes live
 * @returns {null | {ok: true, kind, index, jump} | {ok: false, reason}}
 *          `null` means NOT OURS — a plain click belongs to the drawing tools and the bar
 *          selection, and returning a refusal for one would fill the HUD with complaints about
 *          gestures aimed at something else.
 */
export function resolveChartClick({
  event, pen = DEFAULT_CLICK_PEN, barIndex, playheadIndex, barCount, enabled = true,
} = {}) {
  const e = event || {};
  // NOT OURS unless Alt is held and nothing else is. Alt+Shift would be caught by the spiral
  // handler before this; Alt+Ctrl is a context menu on one platform. One combination, exactly.
  if (!e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return null;
  // Also not ours when the chart resolved no bar at all — a click on the margins or the volume
  // pane is a miss, not a refusal.
  if (!Number.isFinite(barIndex)) return null;
  if (!Number.isFinite(barCount) || barIndex < 0 || barIndex >= barCount) return null;

  if (!enabled) {
    return { ok: false, reason: 'the session is not accepting orders' };
  }
  if (!CLICK_PENS.includes(pen)) {
    return { ok: false, reason: `unknown pen ${String(pen)}` };
  }

  const here = Number.isFinite(playheadIndex) ? playheadIndex : 0;
  if (barIndex < here) {
    // THE HINDSIGHT REFUSAL. Named in full, because a reader who has just clicked a bar they can
    // see needs to be told why that is the problem rather than merely that it did not work.
    return {
      ok: false,
      reason: 'that bar is behind the playhead — its outcome is already on screen, so trading it '
        + 'would be hindsight. Click at or ahead of the playhead.',
    };
  }

  return {
    ok: true,
    // THE PEN NAMES ARE THE ACTION NAMES, deliberately: `applyHumanAction` takes exactly these
    // three words, so there is no translation table to get wrong and a click and a key press
    // reach the reducer as the same value.
    kind: pen,
    index: barIndex,
    // Whether the playhead has to move first. A click on the playhead's own bar is exactly the
    // keyboard order, and the caller can skip the jump — which matters, because a jump pauses the
    // clock and a reader trading during playback did not ask for that.
    jump: barIndex > here,
  };
}

export default resolveChartClick;
